import {
  BackButton,
  Container,
  ContentContainer,
  EmptyState,
  Greeting,
  LoadMoreButton,
  MessageCenterContainer,
  MessageList,
  NavbarContainer,
  NavbarTitle,
  SearchContainer,
  SearchInput,
  Title
} from './message-center.styled';
import { MessageCardProps, NotificationState } from '../../../models/notification-model';
import React, { useCallback, useMemo, useState } from 'react';

import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { CheckInSummaryState } from '../../../redux/reducers/checkInSummarySlice';
import MessageCardComponent from './message-card/message-card.component';
import { RootState } from '../../../config/redux/store';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

function parseUtcLikeToLocalDate(timestamp: string): Date | null {
  if (!timestamp) return null;
  const raw = String(timestamp).trim();

  // If it already has a timezone (Z or +HH:MM / -HH:MM), use as-is
  if (/Z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  }

  // Handle explicit ' UTC' suffix
  if (/\sUTC$/i.test(raw)) {
    const iso = raw.replace(/\sUTC$/i, 'Z');
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  // If it's ISO without timezone like YYYY-MM-DDTHH:mm(:ss)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(raw)) {
    const d = new Date(raw + 'Z');
    return isNaN(d.getTime()) ? null : d;
  }

  // If it's space separated without timezone like YYYY-MM-DD HH:mm(:ss)
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
    const iso = raw.replace(' ', 'T') + 'Z';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  // Fallback: let Date parse; browser will treat as local or best-effort
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function getMessageTimestamp(message: any): string {
  if (!message) return '';

  // Use updatedAt as the primary timestamp, fallback to createdAt
  return message.updatedAt ?? message.createdAt ?? '';
}

export function formatMessageTimestamp(timestamp: string | null): string {
  if (!timestamp) return 'Unknown date';

  try {
    const date = parseUtcLikeToLocalDate(timestamp);
    if (!date) return 'Unknown date';
    const dateStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${dateStr} ${timeStr}`;
  } catch {
    return 'Unknown date';
  }
}

// Memoized message card component for better performance
const MemoizedMessageCard = React.memo(MessageCardComponent);

const MessageCenter: React.FC = () => {
  const navigate = useNavigate();

  // Performance optimization state
  const [searchTerm, setSearchTerm] = useState('');
  const [displayedCount, setDisplayedCount] = useState(20); // Show 20 messages initially
  const MESSAGES_PER_PAGE = 20;
  const MAX_INITIAL_RENDER = 50; // Maximum messages to render initially for performance

  // Get messages from Redux store
  const { messages, isLoading } = useSelector<RootState, NotificationState>(
    (state) => state.globalVendorCheckIn.notifications
  );

  // Get vendor info from check-in summary
  const { checkInSummary } = useSelector<RootState, CheckInSummaryState>(
    (state) => state.globalVendorCheckIn.checkInSummary
  );

  // Get vendor name directly from check-ins API response
  const vendorName = checkInSummary.checkIns?.[0]?.vendorUser?.firstName || '';
  const vendorLastName = checkInSummary.checkIns?.[0]?.vendorUser?.lastName || '';

  // Memoized filtered messages based on search term
  const filteredMessages = useMemo(() => {
    if (!searchTerm.trim()) return messages;

    const searchLower = searchTerm.toLowerCase();
    return messages.filter(
      (message) =>
        message.subject.toLowerCase().includes(searchLower) ||
        message.body.toLowerCase().includes(searchLower) ||
        message.purposeName.toLowerCase().includes(searchLower)
    );
  }, [messages, searchTerm]);

  // Memoized displayed messages with pagination
  const displayedMessages = useMemo(() => {
    // If there are too many messages, limit initial render for performance
    const maxToShow =
      messages.length > MAX_INITIAL_RENDER ? Math.min(displayedCount, MAX_INITIAL_RENDER) : displayedCount;

    return filteredMessages.slice(0, maxToShow);
  }, [filteredMessages, displayedCount, MAX_INITIAL_RENDER, messages.length]);

  // Check if there are more messages to load
  const hasMoreMessages = filteredMessages.length > displayedCount;

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      setDisplayedCount(MESSAGES_PER_PAGE); // Reset pagination when searching
    },
    [MESSAGES_PER_PAGE]
  );

  // Handle load more messages
  const handleLoadMore = useCallback(() => {
    setDisplayedCount((prev) => prev + MESSAGES_PER_PAGE);
  }, [MESSAGES_PER_PAGE]);

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  // Transform messages to card props
  const transformToCardProps = (message: any): MessageCardProps => ({
    messageId: message.messageId,
    store: message.storeId,
    date: formatMessageTimestamp(message.createdAt),
    message: message.body,
    subject: message.subject,
    purposeName: message.purposeName,
    acknowledged: message.isRead
  });

  return (
    <MessageCenterContainer>
      <NavbarContainer>
        <BackButton onClick={handleBackClick} aria-label='Go back' title='Go back to previous page'>
          <ArrowLeftIcon />
        </BackButton>
        <NavbarTitle>Message Center</NavbarTitle>
        <div style={{ width: '40px' }} /> {/* Spacer to center title */}
      </NavbarContainer>

      <Container>
        <Greeting>
          Hi, {vendorName}
          {vendorLastName && ` ${vendorLastName}`}!
        </Greeting>
        <Title>Message Center</Title>

        <ContentContainer>
          {isLoading && (
            <EmptyState>
              <h3>Loading messages...</h3>
              <p>Please wait while we fetch your notifications.</p>
            </EmptyState>
          )}

          {!isLoading && messages.length > 0 && (
            <>
              <SearchContainer>
                <SearchInput
                  type='text'
                  placeholder='Search messages...'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label='Search messages'
                />
              </SearchContainer>

              <MessageList>
                {displayedMessages.map((message) => (
                  <MemoizedMessageCard key={message.messageId} {...transformToCardProps(message)} />
                ))}
              </MessageList>

              {hasMoreMessages && (
                <LoadMoreButton onClick={handleLoadMore}>
                  Load More Messages ({filteredMessages.length - displayedCount} remaining)
                </LoadMoreButton>
              )}

              {filteredMessages.length === 0 && searchTerm && (
                <EmptyState>
                  <h3>No messages found</h3>
                  <p>No messages match your search "{searchTerm}". Try different keywords.</p>
                </EmptyState>
              )}
            </>
          )}

          {!isLoading && messages.length === 0 && (
            <EmptyState>
              <h3>No messages</h3>
              <p>You're all caught up! Check back later for updates.</p>
            </EmptyState>
          )}
        </ContentContainer>
      </Container>
    </MessageCenterContainer>
  );
};

export default MessageCenter;
